import * as Sentry from '@sentry/react';
import { useAtom } from 'jotai';
import { ReactEventHandler, RefObject, useCallback, useEffect, useRef } from 'react';
import { PanelImperativeHandle, PanelSize } from 'react-resizable-panels';
import { ChatBotDrawerDefaultWidth } from './ChatBotSection';
import { ShowChatBotAtom } from './chatbot-state';

const ExpectedGroupErrorPattern = /group .+ not found/i;

// Type assertion needed due to library typing quirk
type OnPanelResize = ReactEventHandler & ((panelSize: PanelSize, id: string | number | undefined) => void);

/** Syncs ShowChatBotAtom with the panel's collapsed/expanded state. */
export function useChatBotPanelStateSync(panelRef: RefObject<PanelImperativeHandle | null>) {
  const [showChatBot, setShowChatBot] = useAtom(ShowChatBotAtom);
  // avoid feedback loops
  const isEffectTriggeredResizeRef = useRef(false);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    try {
      const isCurrentlyCollapsed = panel.isCollapsed();
      if (showChatBot && isCurrentlyCollapsed) {
        isEffectTriggeredResizeRef.current = true;
        panel.resize(ChatBotDrawerDefaultWidth);
      } else if (!showChatBot && !isCurrentlyCollapsed) {
        isEffectTriggeredResizeRef.current = true;
        panel.collapse();
      }
    } catch (error) {
      // Group may not be registered yet during initial mount
      // The state will sync on next effect run
      const isExpectedTimingError = error instanceof Error && ExpectedGroupErrorPattern.test(error.message);

      if (!isExpectedTimingError) {
        Sentry.logger.error((error as Error).message);
      }
    }
  }, [showChatBot, panelRef]);

  const onResize: OnPanelResize = useCallback(
    ((panelSize: PanelSize, _id: string | number | undefined) => {
      if (isEffectTriggeredResizeRef.current) {
        isEffectTriggeredResizeRef.current = false;
        return;
      }

      const isCollapsed = panelSize.inPixels === 0;
      if (isCollapsed && showChatBot) {
        setShowChatBot(false);
      } else if (!isCollapsed && !showChatBot) {
        setShowChatBot(true);
      }
    }) as OnPanelResize,
    [showChatBot, setShowChatBot]
  );

  return onResize;
}
